import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ConsentService } from './consent.service';
import { LoginService } from './login.service';
import { Notification, ConsentRequestNotification } from '@models/notification.model';
import { ConsentRequestSummary, NotificationStatus } from '@models/consent.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly consentService = inject(ConsentService);
  private readonly loginService = inject(LoginService);
  private readonly router = inject(Router);

  private notifications$ = new BehaviorSubject<Notification[]>([]);

  constructor() {
    console.log('NotificationService initialized');
    this.initializeNotifications();
  }

  /**
   * Get all notifications as an Observable
   */
  getNotifications(): Observable<Notification[]> {
    return this.notifications$.asObservable();
  }

  /**
   * Get only new (unread) notifications for header/bell icon
   */
  getNewNotifications(): Observable<Notification[]> {
    return this.notifications$.pipe(
      map(notifications => notifications.filter(n => !n.isRead))
    );
  }

  /**
   * Get count of unread notifications
   */
  getUnreadCount(): Observable<number> {
    return this.notifications$.pipe(
      map(notifications => notifications.filter(n => !n.isRead).length)
    );
  }

  /**
   * Mark a notification as read
   */
  markAsRead(notificationId: string): void {
    const currentNotifications = this.notifications$.value;
    const notification = currentNotifications.find(n => n.id === notificationId);
    
    if (notification && 'consentRequestId' in notification) {
      // Update the notification status via API
      this.consentService.updateNotificationStatus(
        (notification as ConsentRequestNotification).consentRequestId, 
        NotificationStatus.VIEWED
      ).subscribe({
        next: (success) => {
          if (success) {
            // Refresh notifications to get updated status from server
            this.refreshNotifications();
          }
        },
        error: (error) => {
          console.error('Failed to update notification status:', error);
          // Still refresh to get the current state from server
          this.refreshNotifications();
        }
      });
    } else {
      // For non-consent request notifications, just update local state
      const updatedNotifications = currentNotifications.map(n =>
        n.id === notificationId ? { ...n, isRead: true } : n
      );
      this.notifications$.next(updatedNotifications);
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    const currentNotifications = this.notifications$.value;
    const consentRequestNotifications = currentNotifications.filter(n => 'consentRequestId' in n) as ConsentRequestNotification[];
    
    if (consentRequestNotifications.length > 0) {
      // Update all consent request notifications via API
      const updatePromises = consentRequestNotifications.map(notification =>
        this.consentService.updateNotificationStatus(notification.consentRequestId, NotificationStatus.VIEWED).toPromise()
      );
      
      Promise.all(updatePromises).then(results => {
        console.log('Bulk notification status update completed');
        // Refresh notifications to get updated status from server
        this.refreshNotifications();
      }).catch(error => {
        console.error('Some notification status updates failed:', error);
        // Still refresh to get the current state from server
        this.refreshNotifications();
      });
    } else {
      // If no consent request notifications, just update local state
      const updatedNotifications = currentNotifications.map(notification => ({
        ...notification,
        isRead: true
      }));
      this.notifications$.next(updatedNotifications);
    }
  }

  /**
   * Remove a notification
   */
  removeNotification(notificationId: string): void {
    const currentNotifications = this.notifications$.value;
    const notification = currentNotifications.find(n => n.id === notificationId);
    
    if (notification && 'consentRequestId' in notification) {
      // Update the notification status to DISMISSED via API
      this.consentService.updateNotificationStatus(
        (notification as ConsentRequestNotification).consentRequestId, 
        NotificationStatus.DISMISSED
      ).subscribe({
        next: (success) => {
          if (success) {
            // Refresh notifications to get updated status from server
            this.refreshNotifications();
          }
        },
        error: (error) => {
          console.error('Failed to dismiss notification:', error);
          // Still refresh to get the current state from server
          this.refreshNotifications();
        }
      });
    } else {
      // For non-consent request notifications, just remove from local state
      const updatedNotifications = currentNotifications.filter(n => n.id !== notificationId);
      this.notifications$.next(updatedNotifications);
    }
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications(): void {
    const currentNotifications = this.notifications$.value;
    const consentRequestNotifications = currentNotifications.filter(n => 'consentRequestId' in n) as ConsentRequestNotification[];
    
    if (consentRequestNotifications.length > 0) {
      // Update all consent request notifications to CLEARED via API
      const updatePromises = consentRequestNotifications.map(notification =>
        this.consentService.updateNotificationStatus(notification.consentRequestId, NotificationStatus.CLEARED).toPromise()
      );
      
      Promise.all(updatePromises).then(results => {
        console.log('Bulk notification clear completed');
        // Refresh notifications to get updated status from server instead of clearing local state
        this.refreshNotifications();
      }).catch(error => {
        console.error('Some notification clear operations failed:', error);
        // Still refresh to get the current state from server
        this.refreshNotifications();
      });
    } else {
      // If no consent request notifications, just clear local state
      this.notifications$.next([]);
    }
  }

  /**
   * Refresh notifications - fetch latest consent requests
   */
  refreshNotifications(): void {
    this.loadConsentRequestNotifications();
  }

  /**
   * Reload notifications - force refresh of all notifications
   */
  reloadNotifications(): void {
    this.loadConsentRequestNotifications();
  }

  /**
   * Initialize notifications by loading consent requests
   */
  private initializeNotifications(): void {
    // Only load notifications when user is logged in
    this.loginService.isLoggedIn().subscribe((isLoggedIn: boolean) => {
      if (isLoggedIn) {
        this.loadConsentRequestNotifications();
      } else {
        this.clearAllNotifications();
      }
    });
  }

  /**
   * Load consent request notifications from the API
   */
  private loadConsentRequestNotifications(): void {
    this.consentService.getConsentRequestByUser().pipe(
      map(consentRequests => this.convertConsentRequestsToNotifications(consentRequests)),
      tap(notifications => {
        console.log(`Loaded ${notifications.length} consent request notifications`);
      })
    ).subscribe({
      next: (notifications) => {
        this.notifications$.next(notifications);
      },
      error: (error) => {
        console.error('Error loading consent request notifications:', error);
        // Don't clear existing notifications on error, just log it
      }
    });
  }

  /**
   * Convert consent request summaries to notifications
   */
  private convertConsentRequestsToNotifications(consentRequests: ConsentRequestSummary[]): ConsentRequestNotification[] {
    return consentRequests
      .filter(request => request.status === 'PENDING' && request.notificationStatus !== NotificationStatus.DISMISSED)
      .map(request => ({
        id: `consent-request-${request.consentRequestId}`,
        consentRequestId: request.consentRequestId,
        agentOrg: request.agentOrg,
        meterCount: request.meterCount,
        text: `You have a request from '${request.agentOrg}' to share ${request.meterCount} meter(s)`,
        action: () => this.navigateToMeterDetails(request.consentRequestId),
        type: 'info' as const,
        timestamp: new Date(request.requestedDate),
        isRead: request.notificationStatus === NotificationStatus.VIEWED || request.notificationStatus === NotificationStatus.CLEARED
      }));
  }

  /**
   * Navigate to meter details page with consent request ID
   */
  private navigateToMeterDetails(consentRequestId: string): void {
    this.router.navigate(['/meter-details'], {
      queryParams: { consentRequestId }
    });
  }

  /**
   * Update notification status for a specific consent request
   * @param consentRequestId The consent request ID
   * @param status The new notification status
   */
  updateNotificationStatus(consentRequestId: string, status: NotificationStatus): Observable<boolean> {
    return this.consentService.updateNotificationStatus(consentRequestId, status).pipe(
      tap(success => {
        if (success) {
          // Refresh notifications to reflect the change
          this.refreshNotifications();
        }
      })
    );
  }

  /**
   * Mark a consent request notification as viewed and refresh notifications
   * @param consentRequestId The consent request ID
   */
  markConsentRequestAsViewed(consentRequestId: string): Observable<boolean> {
    return this.updateNotificationStatus(consentRequestId, NotificationStatus.VIEWED);
  }

  /**
   * Dismiss a consent request notification and refresh notifications
   * @param consentRequestId The consent request ID
   */
  dismissConsentRequest(consentRequestId: string): Observable<boolean> {
    return this.updateNotificationStatus(consentRequestId, NotificationStatus.DISMISSED);
  }

  /**
   * Clear a consent request notification and refresh notifications
   * @param consentRequestId The consent request ID
   */
  clearConsentRequest(consentRequestId: string): Observable<boolean> {
    return this.updateNotificationStatus(consentRequestId, NotificationStatus.CLEARED);
  }
}
